import React from "react";
import { useEffect, useRef, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import EmptyModal from "../../components/modals/EmptyModal";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { isReadOnlyUser } from "../../shared/utility";

import FilterDropdown from "../../components/filterDropdown/FilterDropdown";
import { InventoryViewModel } from "../../viewmodels/inventory/InventoryViewModel";
import InventorySubTable1 from "./subtables/InventorySubTable1";
import ButtonSpinner from "../../components/controls/ButtonSpinner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import SlideOver from "../../components/slideOver/SlideOver";

const Inventory = (props) => {
  const { logout } = props;

  const {
    emptyModal,
    setEmptyModelOpen,
    getInventory,
    loading,
    inventoryData,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getWarehouseLocations,
    getAllPlus,
    getClassTypes,
    getCategoryTypes,
    printInventorySummaryReport,
    printSubItems,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    openAdjustPriceModal,
    adjustInProgress,
    totalProducts,
    slideOver,
    setSlideOver,
    openSlideForManufacturedItemDetails,
  } = InventoryViewModel(logout);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    getInventory(null, currentPage, filters);
    getWarehouseLocations();
    getAllPlus();
    getClassTypes();
    getCategoryTypes();
  }, []);

  const [expandedRows, setExpandedRows] = useState(null);

  const allowExpansion = (rowData) => {
    return true;
  };

  const handleSearch = () => {
    getInventory(searchRef.current.value, 1, filters);
  };

  const searchRef = useRef();

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const bodyProductName = (item) => {
    return (
      <div>
        {item.product_name} <br />{" "}
        <span className="font-bold">PLU: {item.plu}</span>
      </div>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <p>
        {item.total_weight_in_kg} kg <br />
        {item.total_weight_in_lbs} lbs
      </p>
    );
  };

  const parentClassCategory = (item) => {
    return (
      <p>
        {item.class_name}/{item.category_name}
      </p>
    );
  };

  const parentTotalCartons = (item) => {
    return <p>{item.total_cartons} cartons</p>;
  };

  const bodyParentTotalPieces = (item) => {
    return <p>{item.total_pieces} pieces</p>;
  };

  const bodyParentWarehouseLocation = (item) => {
    return <p>{item.warehouse_location_name}</p>;
  };

  function rowExpansionTemplate(data) {
    return (
      <InventorySubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        openAdjustPriceModal={openAdjustPriceModal}
        adjustInProgress={adjustInProgress}
        openSlideForManufacturedItemDetails={
          openSlideForManufacturedItemDetails
        }
      />
    );
  }

  const handleLoadMoreData = async () => {
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await getInventory(searchRef.current.value, nextPage, filters);
    setLoadingMore(false);
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Inventory
              </h1>
              <div className="mt-5 flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:rounded-md">
                <div className="relative flex items-stretch flex-grow sm:flex-grow-0 sm:w-80 focus-within:z-10">
                  <input
                    type="search"
                    name="searchInventory"
                    id="searchInventory"
                    className="block w-full rounded-lg rounded-l-md pl-2 py-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search inventory"
                    ref={searchRef}
                    onKeyDown={(e) =>
                      e.key === "Enter" ? handleSearch() : null
                    }
                  />
                </div>
                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>

                <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:ml-2">
                  <button
                    className="rounded-md bg-gray-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    onClick={() =>
                      printInventorySummaryReport(
                        searchRef.current.value,
                        filters
                      )
                    }
                  >
                    Print page
                  </button>
                  {!isReadOnlyUser() && (
                    <button
                      onClick={() => {
                        openAdjustPriceModal();
                      }}
                      className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      <ButtonSpinner
                        spin={adjustInProgress}
                        text="Adjust price"
                      />
                    </button>
                  )}
                </div>

                <FilterDropdown
                  actionCall={() =>
                    getInventory(searchRef.current.value, 1, filters)
                  }
                  filterComponents={filterComponents}
                  resetFilters={() => {
                    resetFilters();
                  }}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <TableLoadingSkeleton rows={5} columns={4} />
                ) : (
                  <>
                    <div className="overflow-hidden card-custom">
                      <DataTable
                        value={inventoryData}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => {
                          setExpandedRows(e.data);
                        }}
                        onRowExpand={onRowExpand}
                        rowExpansionTemplate={rowExpansionTemplate}
                        dataKey="unique_id"
                        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm inventory-items-table"
                        cellClassName="p-2 border-1-bottom-gray text-sm"
                      >
                        <Column
                          header="Product"
                          body={bodyProductName}
                          style={{ width: "20%" }}
                        />
                        <Column
                          header="Class/Category"
                          body={parentClassCategory}
                          style={{ width: "10%" }}
                        />
                        <Column
                          header="Warehouse Location"
                          body={bodyParentWarehouseLocation}
                          style={{ width: "10%" }}
                        />
                        <Column
                          header="Carton Count"
                          body={parentTotalCartons}
                          style={{ width: "8%", textAlign: "right" }}
                        />
                        <Column
                          header="Total Weight"
                          body={bodyParentTotalWeight}
                          style={{ width: "8%", textAlign: "right" }}
                        />
                        <Column
                          header="Total Pieces"
                          body={bodyParentTotalPieces}
                          style={{ width: "8%", textAlign: "right" }}
                        />
                        {/* <Column
                        header=""
                        body={printParentSubItems}
                        style={{ width: "5%" }}
                      /> */}
                        <Column
                          expander={allowExpansion}
                          style={{ width: "6%" }}
                        />
                      </DataTable>
                    </div>
                    {inventoryData.length < totalProducts ? (
                      <div className="mt-4 text-center">
                        <button
                          onClick={handleLoadMoreData}
                          disabled={loadingMore}
                          className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ButtonSpinner spin={loadingMore} text="Load More" />
                        </button>
                      </div>
                    ) : (
                      <p className="mt-4 text-center text-gray-500">
                        No more inventory to load
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />

      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
        customClass="max-w-3xl"
      />
    </div>
  );
};

export default Inventory;
